/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PageTerminalIndexResponse } from '../models/PageTerminalIndexResponse';
import type { QrImageDownloadResponse } from '../models/QrImageDownloadResponse';
import type { TerminalDetailResponse } from '../models/TerminalDetailResponse';
import type { TerminalEditForm } from '../models/TerminalEditForm';
import type { TerminalIndexDownloadResponse } from '../models/TerminalIndexDownloadResponse';
import type { TerminalSearchForm } from '../models/TerminalSearchForm';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class TerminalRepository {

  /**
   * 端末一覧表示
   * @returns PageTerminalIndexResponse OK
   * @throws ApiError
   */
  public static index({
    externalTerminalIds,
    terminalIds,
    terminalTypes,
    statuses,
    companyIds,
    shopIds,
    createdAtStartDateTime,
    createdAtEndDateTime,
    onlyDefaultTerminal,
    page = 1,
    size = 20,
    sort,
  }: {
    /**
     * 外部端末ID（複数指定可能）
     */
    externalTerminalIds: Array<string> | undefined,
    /**
     * 端末ID（複数指定可能）
     */
    terminalIds: Array<string> | undefined,
    /**
     * 端末種別（複数指定可能）
     */
    terminalTypes: Array<'UNSELECTED' | 'PAPER_QR' | 'MERCHANT' | 'PAYMENT_TERMINAL' | 'CHARGE' | 'POS_TERMINAL'> | undefined,
    /**
     *
     * ステータス(複数指定可能)
     *
     * ※利用開始日、利用終了日、現在日時の比較および、ログインの有無でアプリ側で設定
     *
     * | ステータス | 条件 |
     * | --- | --- |
     * | 有効待ち | 現在日時 < 利用開始日 (ログイン状態は問わない) |
     * | 有効 | 利用開始日 < 現在日時 < 利用終了日 かつ,端末がログインされている状態 |
     * | 無効 | 「利用終了日 < 現在日時」または、「利用開始日 < 現在日時 < 利用終了日 かつ,端末がログインされていない状態」 |
     *
     * ※terminal_auth_tokenテーブルのlogin_flgカラムの値がtrueの端末をログイン中とする
     *
     */
    statuses: Array<'WAITING' | 'AVAILABLE' | 'CLOSED'> | undefined,
    /**
     * 企業ID(複数指定可能)
     */
    companyIds: Array<string> | undefined,
    /**
     * 店舗ID(複数指定可能)
     */
    shopIds: Array<string> | undefined,
    /**
     * 登録日時(開始日時)　※msまでの日時が検索に反映される
     */
    createdAtStartDateTime: string | undefined,
    /**
     * 登録日時(終了日時)　※msまでの日時が検索に反映される
     */
    createdAtEndDateTime: string | undefined,
    /**
     * デフォルト端末のみ表示する
     */
    onlyDefaultTerminal: boolean | undefined,
    /**
     * One-based page index (1..N)
     */
    page: number | undefined,
    /**
     * The size of the page to be returned
     */
    size: number | undefined,
    /**
     * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     */
    sort: Array<string> | undefined,
  }): CancelablePromise<PageTerminalIndexResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/terminal',
      query: {
        'externalTerminalIds': externalTerminalIds,
        'terminalIds': terminalIds,
        'terminalTypes': terminalTypes,
        'statuses': statuses,
        'companyIds': companyIds,
        'shopIds': shopIds,
        'createdAtStartDateTime': createdAtStartDateTime,
        'createdAtEndDateTime': createdAtEndDateTime,
        'onlyDefaultTerminal': onlyDefaultTerminal,
        'page': page,
        'size': size,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 端末一覧Csvを生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns TerminalIndexDownloadResponse OK
   * @throws ApiError
   */
  public static downloadTerminal({
    requestBody,
  }: {
    requestBody: TerminalSearchForm,
  }): CancelablePromise<TerminalIndexDownloadResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/terminal/download',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 端末詳細表示
   * @returns TerminalDetailResponse OK
   * @throws ApiError
   */
  public static detail({
    id,
  }: {
    /**
     * 端末ID
     */
    id: string,
  }): CancelablePromise<TerminalDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/terminal/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 端末情報更新
   * @returns any OK
   * @throws ApiError
   */
  public static edit({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: TerminalEditForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/terminal/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 端末の静的MPM用QRコード画像を生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns QrImageDownloadResponse OK
   * @throws ApiError
   */
  public static download({
    id,
    amount,
  }: {
    /**
     * 端末ID
     */
    id: string,
    /**
     * 取引金額
     */
    amount: string | undefined,
  }): CancelablePromise<QrImageDownloadResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/terminal/{id}/download/qr',
      path: {
        'id': id,
      },
      query: {
        'amount': amount,
      },
    });
  }

  /**
   * 端末強制ログアウト
   * @returns any OK
   * @throws ApiError
   */
  public static forceLogout({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/terminal/{id}/logout',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

}
